import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { CONFIG } from "../config/app.config";
import * as contractActions from "../reduxV2/contract/contract.actions";
import * as contractSelectors from "../reduxV2/contract/contract.selectors";
import * as paymentRequestHooks from "../reduxV2/paymentRequest/paymentRequest.hooks";
import { compareContractIds, getContractMandateGroups, sortContractMandateGroups, } from "../utils/contractMandateGroup.utils";
// Stable reference so `items ?? EMPTY_PAYMENT_REQUESTS` doesn't create a new
// array (and cascade into unnecessary recomputation) on every render while
// the hook's `items` is still undefined.
var EMPTY_PAYMENT_REQUESTS = [];
// Every contract with a recurring-collection-eligible payment group shows up as
// the payer on some payment request over time - reuse that existing, already
// tenant-accessible query instead of a dedicated (and currently ACL-restricted)
// payment-order lookup, to derive one group per contract.
export var useContractMandateGroups = function (user) {
    var _a = paymentRequestHooks.usePaged({
        shouldRefetch: true,
        query: {
            limit: CONFIG.DEFAULT_FETCH_LIMIT,
            sort: [
                { field: "dueDateAt", method: "DESC" },
                { field: "id", method: "DESC" },
            ],
            filter: {
                requestPayerAccountId: { in: user === null || user === void 0 ? void 0 : user.accountIds },
            },
        },
    }), fetchedPaymentRequests = _a.items, isFetchingPaymentRequests = _a.isFetching, paymentRequestsFetchError = _a.fetchError, refetchPaymentRequests = _a.refetch;
    var paymentRequests = fetchedPaymentRequests !== null && fetchedPaymentRequests !== void 0 ? fetchedPaymentRequests : EMPTY_PAYMENT_REQUESTS;
    var unsortedContractGroups = React.useMemo(function () { return getContractMandateGroups(paymentRequests); }, [paymentRequests]);
    // Sorted, so the same list of contracts always gives the same key, whatever
    // order the payment requests came back in.
    var contractIdsKey = unsortedContractGroups
        .map(function (group) { return group.contractId; })
        .sort(compareContractIds)
        .join(",");
    var contractIds = React.useMemo(function () { return (contractIdsKey ? contractIdsKey.split(",") : []); }, [contractIdsKey]);
    // The contract details used to be fetched by every row on its own. They are
    // fetched here instead, once per contract, so the list can be sorted on them
    // before any row is shown. The number of requests stays the same.
    var dispatch = useDispatch();
    // State, not a ref: marking a contract as requested has to re-render, so
    // isResolvingContracts below never reads a value the render did not see.
    var _b = React.useState(function () { return new Set(); }), requestedContractIds = _b[0], setRequestedContractIds = _b[1];
    React.useEffect(function () {
        if (contractIds.length === 0) {
            return;
        }
        contractIds.forEach(function (id) {
            dispatch(contractActions.getDetailStart.getAction({ id: id }));
        });
        setRequestedContractIds(function (current) {
            if (contractIds.every(function (id) { return current.has(id); })) {
                return current;
            }
            // shared-frontend builds to ES5, where a Set cannot be spread.
            var next = new Set(current);
            contractIds.forEach(function (id) { return next.add(id); });
            return next;
        });
    }, [contractIds, dispatch]);
    var contracts = useSelector(function (state) {
        return contractIds.map(function (id) { return contractSelectors.getDetail(state, id); });
    }, shallowEqual);
    var isFetchingContracts = useSelector(function (state) {
        return contractIds.map(function (id) { return !!contractSelectors.isFetchingDetail(state, id); });
    }, shallowEqual);
    var contractsById = React.useMemo(function () {
        var result = {};
        contractIds.forEach(function (id, index) {
            result[id] = contracts[index];
        });
        return result;
    }, [contractIds, contracts]);
    // A contract is still on its way when it is not in the store yet and its
    // request has not been sent or has not answered. A failed request counts as
    // answered, so a missing contract can never keep the page loading forever.
    var isResolvingContracts = contractIds.some(function (id, index) {
        return contracts[index] == null &&
            (isFetchingContracts[index] || !requestedContractIds.has(id));
    });
    var contractGroups = React.useMemo(function () { return sortContractMandateGroups(unsortedContractGroups, contractsById); }, [unsortedContractGroups, contractsById]);
    // `items` stays undefined until the API has answered once; an empty list is
    // an answer, a failed request is one too.
    var hasFetchedContractGroups = fetchedPaymentRequests !== undefined || paymentRequestsFetchError != null;
    return {
        contractGroups: contractGroups,
        contractsById: contractsById,
        isFetchingContractGroups: isFetchingPaymentRequests,
        // A failed first load leaves no groups at all, which looks exactly like a
        // tenant without any direct debit - readers need this to tell the two apart.
        contractGroupsFetchError: paymentRequestsFetchError,
        // A mandate switch goes through a plain update-mandate call, which never
        // touches the payment-request cache the groups are derived from, so whoever
        // switches a mandate asks for the page again.
        refetchContractGroups: refetchPaymentRequests,
        hasFetchedContractGroups: hasFetchedContractGroups,
        isResolvingContracts: isResolvingContracts,
    };
};
